// src/hooks/dashboard/useCategorySnapshots.ts

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  categoryCurrentQueryOptions,
  categoryCurrentChangesQueryOptions,
  categoryCurrentRatiosQueryOptions,
  categoryCurrentRollupsQueryOptions,
} from "@/services/queries/dashboards/categoriesQueries";
import type {
  CategoryChangesSnapshot,
  CategoryCurrentSnapshot,
  CategoryRollupsSnapshot,
} from "@/types/dashboards/categories";

export type CategorySnapshotBundle = {
  categoryId: number;
  current: Record<string, number | null>;
  ratios: Record<string, number | null>;
  changes: Record<string, Record<string, { time_window: string; date: string; value: number | null }>>;
  rollups: Record<string, Record<string, { time_window: string; date: string; value: number | null }>>;
};

export function useCategorySnapshots() {
  const currentQuery = useQuery(categoryCurrentQueryOptions());
  const changesQuery = useQuery(categoryCurrentChangesQueryOptions());
  const ratiosQuery = useQuery(categoryCurrentRatiosQueryOptions());
  const rollupsQuery = useQuery(categoryCurrentRollupsQueryOptions());

  const isLoading =
    currentQuery.isLoading ||
    changesQuery.isLoading ||
    ratiosQuery.isLoading ||
    rollupsQuery.isLoading;

  const isError =
    currentQuery.isError ||
    changesQuery.isError ||
    ratiosQuery.isError ||
    rollupsQuery.isError;

  const error =
    (currentQuery.error as Error | undefined) ||
    (changesQuery.error as Error | undefined) ||
    (ratiosQuery.error as Error | undefined) ||
    (rollupsQuery.error as Error | undefined);

  const byCategory = useMemo<Record<number, CategorySnapshotBundle>>(() => {
    const current = (currentQuery.data ?? []) as CategoryCurrentSnapshot[];
    const changes = (changesQuery.data ?? []) as CategoryChangesSnapshot[];
    const ratios = (ratiosQuery.data ?? []) as CategoryCurrentSnapshot[];
    const rollups = (rollupsQuery.data ?? []) as CategoryRollupsSnapshot[];

    const out = new Map<number, CategorySnapshotBundle>();
    const ensure = (id: number): CategorySnapshotBundle => {
      let row = out.get(id);
      if (!row) {
        row = { categoryId: id, current: {}, ratios: {}, changes: {}, rollups: {} };
        out.set(id, row);
      }
      return row;
    };

    current.forEach((c) => {
      const row = ensure(c.category_id);
      row.current = c.metrics || {};
    });
    ratios.forEach((r) => {
      const row = ensure(r.category_id);
      row.ratios = r.metrics || {};
    });
    changes.forEach((c) => {
      const row = ensure(c.category_id);
      row.changes = c.metrics || {};
    });
    rollups.forEach((r) => {
      const row = ensure(r.category_id);
      row.rollups = r.metrics || {};
    });

    return Object.fromEntries(out.entries());
  }, [currentQuery.data, changesQuery.data, ratiosQuery.data, rollupsQuery.data]);

  return {
    byCategory,
    isLoading,
    isError,
    error,
  } as const;
}


